import {Box, Button, Card, CardContent, Chip, CircularProgress, Stack, Tooltip, Typography} from '@mui/material';
import {useCallback} from 'react';
import {Question} from '@cp/model';
import MarkdownContentRenderer from '../../../atoms/MarkdownContentRenderer/MarkdownContentRenderer';
import BondGraphRenderer from '../../../bonds/bondGraphRenderer/BondGraphRenderer';
import QuestionFormStatusNoteDialog from '../../../components/QuestionFormDialog/QuestionFormStatusNoteDialog';
import {env} from '../../../config/env';
import {useCheckQuestionWithGpt} from '../../../hooks/question/useCheckQuestionWithGpt';
import {useCheckQuestionWithRules} from '../../../hooks/question/useCheckQuestionWithRules';
import mapModuleTopicToTopicString from '../../../utils/map/mapModuleTopicToTopicString';

const difficultyColors = ['#90BE6D', '#7AAD5A', '#5A8A3F', '#FF6F59', '#E63946'];

export interface QuestionManagementListItemProps {
  question: Question;
  index: number;
  startIndex: number;
  moduleId: string;
  onClick: (question: Question) => void;
}

export default function QuestionManagementListItem({
  question,
  index,
  startIndex,
  moduleId,
  onClick,
}: QuestionManagementListItemProps) {
  const {checkQuestionWithRules, loading: checkRulesLoading} = useCheckQuestionWithRules();
  const {checkQuestionWithGpt, loading: checkGptLoading} = useCheckQuestionWithGpt();

  const handleCheckWithRules = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card click
      checkQuestionWithRules(moduleId, question.id).catch((error) => {
        console.error('Error checking question with rules:', error);
      });
    },
    [moduleId, question.id, checkQuestionWithRules],
  );

  const handleCheckWithGpt = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card click
      checkQuestionWithGpt(moduleId, question.id).catch((error) => {
        console.error('Error checking question with GPT:', error);
      });
    },
    [moduleId, question.id, checkQuestionWithGpt],
  );

  const statusNoteSnippet = (question.statusNote || '').substring(0, env.question.statusNoteLength);

  return (
    <Card
      sx={{
        cursor: 'pointer',
        '&:hover': {bgcolor: '#f5f5f5'},
        border: '1px solid #e0e0e0',
      }}
      onClick={() => onClick(question)}
    >
      <CardContent>
        <Stack direction="column" spacing={1}>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Typography variant="body2" sx={{color: '#666'}}>
              Question {startIndex + index + 1}
            </Typography>
            <Chip
              label={mapModuleTopicToTopicString(question.moduleTopic, {spacer: ' • '})}
              size="small"
              sx={{bgcolor: '#e3f2fd', color: '#1976d2', mb: 0.5}}
            />
            <Chip
              label={`Level ${question.difficulty}`}
              size="small"
              sx={{
                bgcolor: difficultyColors[question.difficulty - 1],
                color: 'white',
              }}
            />

            <Chip
              label={question.status}
              size="small"
              sx={{bgcolor: env.question.status[question.status].color, color: 'white'}}
            />
          </Stack>
          <BondGraphRenderer graphJsonString={question.graphJsonString} height={180} />
          <MarkdownContentRenderer content={question.questionText} />
          {statusNoteSnippet && (
            <Typography variant="body2" sx={{color: '#d32f2f', fontStyle: 'italic'}}>
              Note: {statusNoteSnippet}
            </Typography>
          )}
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Typography variant="body2" sx={{color: '#666'}}>
              {question.options.length} options • Created: {new Date(question.createdAt).toLocaleDateString()}
            </Typography>
            <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Validate question using rule-based checks">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCheckWithRules}
                    disabled={!moduleId || checkRulesLoading || checkGptLoading}
                    sx={{
                      borderColor: '#1B263B',
                      color: '#1B263B',
                      minWidth: 'auto',
                      px: 1.5,
                      '&:hover': {borderColor: '#1B263B', bgcolor: '#1B263B', color: 'white'},
                    }}
                  >
                    {checkRulesLoading ? <CircularProgress size={16} /> : 'Check Rules'}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Validate question using GPT">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCheckWithGpt}
                    disabled={!moduleId || checkRulesLoading || checkGptLoading}
                    sx={{
                      borderColor: '#1B263B',
                      color: '#1B263B',
                      minWidth: 'auto',
                      px: 1.5,
                      '&:hover': {borderColor: '#1B263B', bgcolor: '#1B263B', color: 'white'},
                    }}
                  >
                    {checkGptLoading ? <CircularProgress size={16} /> : 'Check GPT'}
                  </Button>
                </span>
              </Tooltip>
              <Box onClick={(e) => e.stopPropagation()}>
                <QuestionFormStatusNoteDialog statusNote={question.statusNote || ''} />
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
